import * as React from "react";

import type { ClubInClubHomeStatusDto } from "@volley/data";

import fetchApi, { logFetchError } from "../../util/fetchApi";
import { useCurrentUser } from "../hooks/currentUser";

import { CLUB_IN_CLUB_FEATURE } from "./HomeScreenItems";

/**
 * The single "nothing to show" value. A member who is not in Club-in-Club, a disabled
 * integration and an unreachable upstream all land here, because the widget's job in all
 * three cases is identical: render nothing.
 */
const NOT_A_MEMBER: ClubInClubHomeStatusDto = {
    member: null,
    pointBalance: null,
    refreshInDays: null,
    nextSession: null,
    upcomingSessionCount: 0,
    setup: null,
    portalUrl: null,
    volley: { hasPriorSessions: false },
};

export interface HomeClubInClubValue {
    /**
     * Whether this user is in the Club-in-Club cohort — the feature is granted *and* the
     * feature list has resolved, so it is false while that is still in flight rather than
     * asserting an answer it does not have yet.
     *
     * Read by the home screen to decide who owns the top slot, which is why it is on the
     * context rather than each consumer re-deriving it.
     */
    granted: boolean;
    status: ClubInClubHomeStatusDto;
    /**
     * True while the answer could still change. Consumers must withhold the widget
     * rather than render a default — a card that appears and then vanishes, or shows a
     * zero balance before the real one lands, is the flash this exists to prevent.
     */
    loading: boolean;
    /** Re-read on demand. Leaving home for the portal in a browser unmounts the provider,
     *  and coming back remounts it and reads afresh. In the native app the portal opens in
     *  a drawer over a home that stays mounted, so the widget calls this on the way back.
     *
     *  `quiet` re-reads behind what is already shown: `loading` stays false, so the widget
     *  doesn't vanish and reappear, and a failure keeps the last answer rather than hiding
     *  a card the member was just using. */
    refetch: (opts?: RefetchOptions) => Promise<void>;
}

export interface RefetchOptions {
    quiet?: boolean;
}

const HomeClubInClubContext = React.createContext<HomeClubInClubValue | null>(
    null,
);

/**
 * The home screen's Club-in-Club state, fetched once.
 *
 * `GET /api/integrations/club-in-club/home-status` answers the whole widget in one cheap
 * request: balance, next reserved session, membership state, and — for a signed-in
 * non-member — whether they can set an account up.
 *
 * Gated on the feature so nobody outside the pilot cohort issues the request at all. That
 * gate is also what keeps the outbound load on Club-in-Club proportionate to the cohort
 * rather than to Volley's whole user base.
 */
export function HomeClubInClubProvider({
    children,
}: {
    children: React.ReactNode;
}): React.JSX.Element {
    const { features, featuresLoaded } = useCurrentUser();

    // "Not granted" and "not loaded yet" must not look the same: treating an empty
    // feature list as a decision would settle on "hide" and then flip.
    const granted = features.includes(CLUB_IN_CLUB_FEATURE);
    const shouldFetch = featuresLoaded && granted;

    const [status, setStatus] =
        React.useState<ClubInClubHomeStatusDto>(NOT_A_MEMBER);
    // Starts true so consumers withhold on the first render, before any fetch begins.
    const [fetching, setFetching] = React.useState(true);

    // Derived, not stored: "the feature list hasn't resolved" and "a request is in
    // flight" are two independent reasons to withhold, and keeping them in one piece of
    // state meant each writer had to know about the other.
    const loading = !featuresLoaded || fetching;

    // A ref, not an effect-scoped flag: `load` is also called manually via refetch(),
    // whose result can land after unmount just as the initial fetch's can.
    const mounted = React.useRef(true);
    React.useEffect(() => {
        mounted.current = true;
        return () => {
            mounted.current = false;
        };
    }, []);

    const load = React.useCallback(
        async ({ quiet }: RefetchOptions = {}) => {
            if (!shouldFetch) {
                // Nothing to wait for. Whether consumers still withhold is `featuresLoaded`'s
                // business, not ours.
                if (mounted.current) {
                    setStatus(NOT_A_MEMBER);
                    setFetching(false);
                }
                return;
            }
            if (!quiet) setFetching(true);
            try {
                const res = await fetchApi<ClubInClubHomeStatusDto>(
                    "/api/integrations/club-in-club/home-status",
                );
                if (mounted.current) setStatus(res);
            } catch (err) {
                // No error state: Club-in-Club standing is supplementary on this screen, so a
                // failure degrades to an absent widget rather than interrupting the home
                // screen. The server already collapses upstream failures into the same shape;
                // this covers our own request failing.
                logFetchError(err, "Failed to load Club-in-Club home status");
                if (mounted.current && !quiet) setStatus(NOT_A_MEMBER);
            } finally {
                if (mounted.current && !quiet) setFetching(false);
            }
        },
        [shouldFetch],
    );

    React.useEffect(() => {
        void load();
    }, [load]);

    const value = React.useMemo(
        () => ({ granted: shouldFetch, status, loading, refetch: load }),
        [shouldFetch, status, loading, load],
    );

    return (
        <HomeClubInClubContext.Provider value={value}>
            {children}
        </HomeClubInClubContext.Provider>
    );
}

/** The home screen's shared Club-in-Club state. Must be used under the provider. */
export function useHomeClubInClub(): HomeClubInClubValue {
    const context = React.useContext(HomeClubInClubContext);
    if (!context) {
        throw new Error(
            "useHomeClubInClub must be used within HomeClubInClubProvider",
        );
    }
    return context;
}
